/**
 * Copyright (c) 2025 Stefan Radig
 *
 * Distinguishing "could not reach the server" from "the server said no".
 *
 * This matters because the two need opposite handling: an unreachable server
 * is normal (we have an IndexedDB cache and can carry on), while a server that
 * answers with an error is a real problem the user should see.
 *
 * `navigator.onLine` cannot make this distinction - it reports whether a
 * network interface exists, so it stays true when the LAN is up but the server
 * is down. The reliable signal is how fetch() failed.
 */

/**
 * True when a thrown value means the request never reached the server.
 *
 * fetch() rejects with a TypeError only when the request could not be made at
 * all (DNS failure, connection refused, TLS failure, CORS preflight failure).
 * Any actual response - including 4xx and 5xx - resolves normally. So a
 * TypeError from fetch is an unambiguous "server unreachable", regardless of
 * the browser-specific message ("Failed to fetch", "Load failed", ...).
 *
 * Aborted and timed-out requests count as unreachable too: we have no answer
 * from the server, which is the condition callers care about.
 */
export function isNetworkError(err: unknown): boolean {
    if (err instanceof TypeError) return true;

    if (err instanceof Error) {
        // DOMException for abort/timeout; matched by name so this works
        // wherever DOMException is not a global.
        return err.name === 'AbortError' || err.name === 'TimeoutError';
    }

    return false;
}

/**
 * True when an HTTP status means the *backend* is down rather than that it
 * refused the request.
 *
 * Behind a reverse proxy this is the common case, and it is easy to miss: the
 * proxy stays up and answers on the backend's behalf, so fetch() resolves
 * normally. With php-fpm stopped, Caddy returns 502 with an empty body.
 *
 * 500 is deliberately excluded - it means PHP ran and threw, which is a real
 * fault the user should see rather than have hidden behind an offline
 * indicator.
 */
export function isServerDownStatus(status: number): boolean {
    return status === 502 || status === 503 || status === 504;
}
